import React, { Children } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@ohif/ui-next';

// Route Components
import DataSourceWrapper from './DataSourceWrapper';
import WorkList from './WorkList';
import Local from './Local';
import Debug from './Debug';
import NotFound from './NotFound';
import buildModeRoutes from './buildModeRoutes';
import PrivateRoute from './PrivateRoute';
import PropTypes from 'prop-types';
import { routerBasename } from '../utils/publicUrl';
import { useAppConfig } from '@state';
import { history } from '../utils/history';
import StudyWorkList from "../pages/StudyWorkList";
import LoginPage from "../pages/Login";
import SingleSignOnPage from "../pages/SingleSignOn";
import DicomUploadPage from "../pages/DicomUploadPage"
import SpinalCordViewer from "../pages/Stl3DModel"
import MainPage from "../pages/Main"
import Dashboard from "../pages/Dashboard"
import PatientList from '../components/PatientList';
import PatientFormData from '../components/CreatePatient'
import AddUser from '../components/CreateUser'
import ComparisonPage from '../pages/ComparisonPage'
import ChangePasswordPage from '../pages/ChangePassword'

const NotFoundServer = ({
  message = 'Unable to query for studies at this time. Check your data source configuration or network connection',
}) => {
  return (
    <div className="absolute flex h-full w-full items-center justify-center text-white">
      <div>
        <h4>{message}</h4>
      </div>
    </div>
  );
};

NotFoundServer.propTypes = {
  message: PropTypes.string,
};

const NotFoundStudy = () => {
  const [appConfig] = useAppConfig();
  const { showStudyList } = appConfig;

  return (
    <div className="absolute flex h-full w-full items-center justify-center text-white">
      <div>
        <h4>
          One or more of the requested studies are not available at this time.
        </h4>
        {showStudyList && (
          <p className="mt-2">
            Return to the <Link className="text-primary-light" to="/">study list</Link> to select a different study to view.
          </p>
        )}
      </div>
    </div>
  );
};

NotFoundStudy.propTypes = {
  message: PropTypes.string,
};

// TODO: Include "routes" debug route if dev build
const bakedInRoutes = [
  {
    path: `/notfoundserver`,
    children: NotFoundServer,
  },
  {
    path: `/notfoundstudy`,
    children: NotFoundStudy,
  },
  {
    path: `/debug`,
    children: Debug,
  },
  {
    path: `/local`,
    children: Local.bind(null, { modePath: '' }), // navigate to the worklist
  },
  {
    path: `/localbasic`,
    children: Local.bind(null, { modePath: 'viewer/dicomlocal' }),
  },
  {
    path: `/localWorklist`,
    children: StudyWorkList,
  },
  {
    path: `/localWorklistUpload`,
    children: DicomUploadPage,
  },
  {
    path: `/localStls`,
    children:  SpinalCordViewer,
  },
  {
    path: `/login`,
    children:  LoginPage,
  },
  {
    path: `/sso`,
    children:  SingleSignOnPage,
  },
  {
    path: `/admin`,
    children: Dashboard
  },
  {
    path: `/change-password`,
    children: ChangePasswordPage
  },
  {
    path: `/patients`,
    children: PatientList
  },
  {
    path: `/addPatient`,
    children: PatientFormData
  },
  {
    path: `/addUser`,
    children: AddUser
  },
  {
    path: `/comparison`,
    children: ComparisonPage
  }
];

// NOT FOUND (404)
const notFoundRoute = { component: NotFound };

const createRoutes = ({
  modes,
  dataSources,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
  showStudyList,
}: withAppTypes) => {
  const routes =
    buildModeRoutes({
      modes,
      dataSources,
      extensionManager,
      servicesManager,
      commandsManager,
      hotkeysManager,
    }) || [];

  const { customizationService } = servicesManager.services;

  const path =
    routerBasename.length > 1 && routerBasename.endsWith('/')
      ? routerBasename.substring(0, routerBasename.length - 1)
      : routerBasename;

  console.log('Registering worklist route', routerBasename, path);

  // const WorkListRoute = {
  //   path: '/',
  //   children: DataSourceWrapper,
  //   private: true,
  //   props: { children: WorkList, servicesManager, extensionManager },
  // };

  const WorkListRoute = {
    path: '/',
    children: MainPage
    // children: StudyWorkList
  };

  const customRoutes = customizationService.getCustomization('routes.customRoutes');

  const allRoutes = [
    ...routes,
    ...(showStudyList ? [WorkListRoute] : []),
    ...(customRoutes?.routes || []),
    ...bakedInRoutes,
    customRoutes?.notFoundRoute || notFoundRoute,
  ];

  function RouteWithErrorBoundary({ route, ...rest }) {
    history.navigate = useNavigate();

    // eslint-disable-next-line react/jsx-props-no-spreading
    return (
      <ErrorBoundary context={`Route ${route.path}`}>
        <route.children
          {...rest}
          {...route.props}
          route={route}
          servicesManager={servicesManager}
          extensionManager={extensionManager}
          hotkeysManager={hotkeysManager}
        />
      </ErrorBoundary>
    );
  }

  const { userAuthenticationService } = servicesManager.services;

  // All routes are private by default and then we let the user auth service
  // to check if it is enabled or not
  // Todo: I think we can remove the second public return below
  return (
    <Routes>
      {allRoutes.map((route, i) => {
        return route.private === true ? (
          <Route
            key={i}
            path={route.path}
            element={
              <PrivateRoute
                handleUnauthenticated={() => userAuthenticationService.handleUnauthenticated()}
              >
                <RouteWithErrorBoundary route={route} />
              </PrivateRoute>
            }
          ></Route>
        ) : (
          <Route
            key={i}
            path={route.path}
            element={<RouteWithErrorBoundary route={route} />}
          />
        );
      })}
    </Routes>
  );
};

export default createRoutes;
